import { ProductAdditionalInfoEntity } from '@genial/services';
import { Utils } from '@genial/utils';

import { formatAvailableAmount } from '../../NewApplication/Product/components/FixedIncome/constants';

import { messageError } from './constants';
import { OrderPdfCommonTypes, OrderPdfTypes } from './types';

type FilterDefinition<T, K extends keyof T> = Partial<
  Record<
    K,
    {
      label?: string;
      // TODO: move "string" to union type later
      value(
        this: T,
        productType?: string
      ): string | number | undefined | null | ProductAdditionalInfoEntity[];
    }
  >
>;

type FieldsByProductType<T> = Record<string, FilterDefinition<T, keyof T>>;

export const defaultTransforms: FilterDefinition<OrderPdfCommonTypes, keyof OrderPdfCommonTypes> = {
  orderName: {
    label: 'Nome da ordem:',
    value() {
      return this.orderName;
    },
  },
  orderDate: {
    label: 'Data de envio:',
    value() {
      return Utils.Mask.DateFormat(this.orderDate, 'dd/MM/yyyy');
    },
  },
  orderAmount: {
    label: 'Valor da ordem',
    value() {
      return Utils.Mask.Money(this.orderAmount);
    },
  },
};

export const transformProductFieldsByProductType: FieldsByProductType<OrderPdfTypes> = {
  FixedIncome: {
    productDisplay: {
      label: 'Produto',
      value() {
        return this.productDisplay;
      },
    },
    custodyAccount: {
      label: 'Conta de custódia',
      value() {
        return this.custodyAccount;
      },
    },
    riskRatingName: {
      label: 'Risco',
      value() {
        return this.riskRatingName;
      },
    },
    maturityDate: {
      label: 'Vencimento',
      value() {
        return Utils.Mask.DateFormat(this.maturityDate, 'dd/MM/yyyy');
      },
    },
    liquidityIndicator: {
      label: 'Liquidez',
      value() {
        return this.liquidityIndicator;
      },
    },
    customerFeeAmount: {
      label: 'Taxa',
      value() {
        return this.customerFeeAmount;
      },
    },
    customerPuAmount: {
      label: 'Preço unitário',
      value() {
        return this.customerPuAmount;
      },
    },
  },
  FixedIncomeRescue: {
    productDisplay: {
      label: 'Produto',
      value() {
        return this.productDisplay;
      },
    },
    accountNumber: {
      label: 'Tipo',
      value() {
        return 'Resgate';
      },
    },
    ir: {
      label: 'IR',
      value() {
        return Utils.Mask.Money(this.ir);
      },
    },
    netFinancialValue: {
      label: 'Valor liquido',
      value() {
        return Utils.Mask.Money(this.netFinancialValue);
      },
    },
    orderQuantity: {
      label: 'Qtd. solicitada',
      value() {
        return formatAvailableAmount(this.orderQuantity!);
      },
    },
    redemptionDate: {
      label: 'Disponível em conta',
      value() {
        const redemptionDate =
          this.redemptionDate && (this.redemptionDate as unknown as string) !== messageError
            ? Utils.Mask.DateFormat(this.redemptionDate ?? new Date(), 'dd/MM/yyyy')
            : messageError;

        return redemptionDate;
      },
    },
  },
  Fund: {
    productDisplay: {
      label: 'Produto',
      value() {
        return this.productDisplay;
      },
    },
    qualifiedInvestor: {
      label: 'Investidor qualiﬁcado',
      value() {
        return this.qualifiedInvestor ? 'SIM' : 'NÃO';
      },
    },
    riskRating: {
      label: 'Risco',
      value() {
        return this.riskRating;
      },
    },
    profitabilityTwelveMonthsAdjusted: {
      label: 'Rentabilidade 12 meses',
      value() {
        return Utils.Mask.Percentage(parseFloat(this.profitabilityTwelveMonthsAdjusted || '0'));
      },
    },
    administrationFee: {
      label: 'Taxa administrativa',
      value() {
        return this.administrationFee;
      },
    },
    quotationRedemptionTermFormatted: {
      label: 'Resgate',
      value() {
        return this.quotationRedemptionTermFormatted || messageError;
      },
    },
    redemptionDescription: {
      label: 'Liquidação',
      value() {
        return this.redemptionDescription;
      },
    },
  },
  FundRedemption: {
    productDisplay: {
      label: 'Produto',
      value() {
        return this.productDisplay;
      },
    },
    orderType: {
      label: 'Tipo',
      value() {
        return 'Resgate';
      },
    },
    orderAmount: {
      label: 'Valor bruto solicitado',
      value() {
        return Utils.Mask.Money(this.orderAmount);
      },
    },
    estimatedNetValue: {
      label: 'Valor líquido estimado',
      value() {
        return this.estimatedNetValue;
      },
    },
    quotationDate: {
      label: 'Data de cot. atual',
      value() {
        return this.quotationDate;
      },
    },
    liquidationDate: {
      label: 'Data de liq. atual',
      value() {
        return this.liquidationDate;
      },
    },
  },
  Equity: {
    productDisplay: {
      label: 'Produto',
      value() {
        return `${this.productName} - ${this.symbol}`;
      },
    },
    operation: {
      label: 'Operação',
      value() {
        return this.operationType || messageError;
      },
    },
    lastPrice: {
      label: 'Último preço',
      value() {
        return `${Utils.Mask.Money(this.lastPrice)}  ${
          this.priceChangeTax && this.priceChangeTax > 0 ? '+' : ''
        }${Utils.Mask.Percentage(this.priceChangeTax)}`;
      },
    },
    tradeDate: {
      value() {
        if (this.tradeDate === messageError) {
          return;
        }
        return `Atualizado às ${Utils.Mask.DateFormat(this.tradeDate as Date, 'HH:mm')}`;
      },
    },
    orderValidity: {
      label: 'Validade da ordem',
      value() {
        return `Hoje (${Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')})`;
      },
    },
    orderType: {
      label: 'Tipo de ordem',
      value() {
        return this.orderType || 'LIMITADA';
      },
    },
    orderQuantity: {
      label: 'Quantidade',
      value() {
        return this.orderQuantity;
      },
    },
    orderAmount: {
      label: 'Valor por ativo',
      value() {
        return Utils.Mask.Money(this.orderPrice);
      },
    },
  },
  EquityRescue: {
    productDisplay: {
      label: 'Produto',
      value() {
        return `${this.productName} - ${this.symbol}`;
      },
    },
    operation: {
      label: 'Operação',
      value() {
        return this.operationType || messageError;
      },
    },
    lastPrice: {
      label: 'Último preço',
      value() {
        return `${Utils.Mask.Money(this.lastPrice)}  ${
          this.priceChangeTax && this.priceChangeTax > 0 ? '+' : ''
        }${Utils.Mask.Percentage(this.priceChangeTax)}`;
      },
    },
    tradeDate: {
      value() {
        if (this.tradeDate === messageError) {
          return;
        }
        return `Atualizado às ${Utils.Mask.DateFormat(this.tradeDate as Date, 'HH:mm')}`;
      },
    },
    orderValidity: {
      label: 'Validade da ordem',
      value() {
        return `Hoje (${Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')})`;
      },
    },
    orderType: {
      label: 'Tipo de ordem',
      value() {
        return this.orderType || 'LIMITADA';
      },
    },
    orderQuantity: {
      label: 'Quantidade',
      value() {
        return this.orderQuantity;
      },
    },
    orderAmount: {
      label: 'Valor por ativo',
      value() {
        return Utils.Mask.Money(this.orderPrice);
      },
    },
  },
  DirectTreasury: {
    productDisplay: {
      label: 'Produto',
      value() {
        return `${this.titleName}`;
      },
    },
    orderDate: {
      label: 'Data da aplicação',
      value() {
        return Utils.Mask.DateFormat(this.orderDate, 'dd/MM/yyyy');
      },
    },
    settlementDate: {
      label: 'Data de liquidação',
      value() {
        return typeof this.settlementDate?.getMonth === 'function'
          ? Utils.Mask.DateFormat(this.settlementDate, 'dd/MM/yyyy')
          : (this.settlementDate as unknown as string);
      },
    },
    maturityDate: {
      label: 'Vencimento',
      value() {
        return Utils.Mask.DateFormat(this.maturityDate, 'dd/MM/yyyy');
      },
    },
    orderQuantity: {
      label: 'Quantidade',
      value() {
        return this.orderQuantity?.toFixed(2);
      },
    },
  },
  DirectTreasuryRescue: {
    productDisplay: {
      label: 'Produto',
      value() {
        return `${this.titleName}`;
      },
    },
    orderType: {
      label: 'Tipo',
      value() {
        return 'Resgate';
      },
    },
    orderAmount: {
      label: 'Valor bruto para resgate',
      value() {
        return Utils.Mask.Money(this.orderAmount);
      },
    },
    orderQuantity: {
      label: 'Qtd. solicitada',
      value() {
        return formatAvailableAmount(this.orderQuantity!);
      },
    },
    redemptionDate: {
      label: 'Disponível em conta',
      value() {
        const redemptionDate =
          this.redemptionDate && (this.redemptionDate as unknown as string) !== messageError
            ? Utils.Mask.DateFormat(this.redemptionDate ?? new Date(), 'dd/MM/yyyy')
            : messageError;

        return redemptionDate;
      },
    },
  },
  StructuredOperations: {
    operationName: {
      label: 'Produto',
      value() {
        return `${this.operationName}`;
      },
    },
    strategy: {
      label: 'Estratégia',
      value() {
        return `${this.strategy}`;
      },
    },
    riskRatingName: {
      label: 'Risco',
      value() {
        return this.riskRatingName;
      },
    },
    maturityDate: {
      label: 'Vencimento',
      value() {
        return Utils.Mask.DateFormat(this.maturityDate, 'dd/MM/yyyy');
      },
    },
    additionalInfo: {
      label: 'additionalInfo',
      value() {
        return this.additionalInfo;
      },
    },
    applicationIn: {
      label: 'Aplicação em',
      value() {
        return this.applicationIn;
      },
    },
  },
};
